import { useCallback, useEffect, useState } from 'react'
import { sortBy, startCase, toLower } from 'lodash-es'
import AddIcon from '@mui/icons-material/Add'

import { ISelectOption } from '$/types'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'

import {
  EditScenarioViewProps,
  IThreatWithCode,
  updateConditionsInRule,
  IRuleWithId
} from './types'
import { EMPTY_RULE } from './emptyRyle'
import { scenarioToRuleWithId } from './scenarioToRuleWithId'
import { ViewModeShell } from './ViewModeShell'
import {
  findLookupTablesFromBaseSettings,
  useGetBaseSettingsQuery
} from '$/app/services/api/basesettings'
import AttributeField from './AttributeField'
import { DROPDOWN_LIST_HEIGHT } from './constants'
import { useValidatorHandlers } from './utils/useValidatorHandlers'

export const EditScenarioView = ({
  scenario,
  scenarioName,
  scenarioRules,
  setScenarioName,
  setScenarioRules,
  scenarioModaltitle,
  scenarioNameValidationMessage,
  isScenarioNameValid,
  setIsScenarioNameEmpty,
  setSelectedThreat,
  selectedThreat
}: EditScenarioViewProps) => {
  const [ruleId, setRuleId] = useState<number>(
    scenario.rules.length === 0 ? 1 : scenario.rules.length
  )

  const { lookupTables } = useGetBaseSettingsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      lookupTables: findLookupTablesFromBaseSettings(data)
    })
  })

  const threatOptions = (lookupTables?.details ?? []).reduce(
    (acc: ISelectOption[], table) => {
      if (table.key === 'Threat') {
        ;(table.value as IThreatWithCode[]).forEach((threat) => {
          if (threat.TypeCode === 'POF') {
            acc.push({
              label: startCase(threat.Name),
              value: threat.Id.toString()
            })
          }
        })
      }

      return acc
    },
    []
  )

  useEffect(() => {
    setScenarioRules(scenarioToRuleWithId(scenario.rules))
  }, [scenario.rules, setScenarioRules])

  const addRule = (): void => {
    setScenarioRules((state) =>
      state.concat({
        ...EMPTY_RULE,
        id: ruleId
      })
    )
    setRuleId(ruleId + 1)
  }

  const removeRule = (id: number): void => {
    setScenarioRules(scenarioRules.filter((rule) => rule.id !== id))
  }

  const editRule = (id: number, newRule: IRuleWithId): void => {
    setScenarioRules((state) =>
      state.map((rule) => {
        if (rule.id === id) {
          return newRule
        }

        return rule
      })
    )
  }

  // ! TEMP Fix for updating condition without loop
  const updateConditionsInRule = useCallback<updateConditionsInRule>(
    (id, conditionsWithId) =>
      setScenarioRules((state) =>
        state.map((rule) => {
          if (rule.id === id) {
            return {
              ...rule,
              conditions: conditionsWithId.map(
                ({ id, ...condition }) => condition
              )
            }
          }

          return rule
        })
      ),
    [setScenarioRules]
  )

  const validatorHandlers = useValidatorHandlers(setIsScenarioNameEmpty)

  const handleScenarioNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const trimmedValue = event.target.value.trim()

    setScenarioName(trimmedValue)
  }

  return (
    <ViewModeShell>
      <div>
        <OverlayScrollbar scrollStyle='h-[500px] pr-3 pl-1'>
          <div className='mb-3'>
            <div className='my-2'>
              <label className='text-3xl font-semibold'>
                {scenarioModaltitle}
              </label>
            </div>
            <label
              htmlFor='scenario-name-input'
              className='pb-2 font-semibold'
            >
              Scenario Name*
            </label>
            <div>
              <input
                id='scenario-name-input'
                className={`mt-1 w-72 rounded-sm ${!isScenarioNameValid ? 'border-red-danger' : ''}`}
                type='text'
                placeholder='E.g. sample state'
                onChange={handleScenarioNameChange}
                value={scenarioName}
                onBlur={validatorHandlers.onFocusOut}
                onFocus={() => setIsScenarioNameEmpty(false)}
              />
              <p className='mt-2 text-sm font-medium text-red-danger'>
                {scenarioNameValidationMessage}
              </p>
            </div>
          </div>
          <div className='mb-3'>
            <label
              htmlFor='threat-input'
              className='pb-2 font-semibold'
            >
              Threat
            </label>
            <div>
              <SelectDropdownWrapper
                id='threat-input'
                className='my-1 w-72'
                listHeight={DROPDOWN_LIST_HEIGHT}
                options={sortBy(threatOptions, 'label')}
                value={selectedThreat.value || null}
                onChangeSelections={(selections: Array<ISelectOption>) => {
                  // REVIEW: the select was set to be clearable
                  // so null value can be set here..
                  if (selections.length) {
                    setSelectedThreat(selections[0])
                  }
                }}
                allowClear={selectedThreat && selectedThreat.label !== ''}
                placeholder='Select threat'
                showSearch
                filterOption={(input, option) =>
                  toLower(option?.label as string).includes(toLower(input))
                }
                onClear={() => setSelectedThreat({ label: '', value: '' })}
              />
            </div>
          </div>
          <div className='mb-3'>
            <label
              htmlFor='attributes-input'
              className='pb-2 font-semibold'
            >
              Attributes*
            </label>
            <div>
              {scenarioRules.map((rule) => {
                return (
                  <AttributeField
                    key={rule.id}
                    selectedThreat={selectedThreat}
                    removeRule={() => removeRule(rule.id)}
                    ruleValues={rule}
                    editRule={(rule: IRuleWithId) => editRule(rule.id, rule)}
                    updateConditionsInRule={updateConditionsInRule}
                  />
                )
              })}
            </div>
            {scenarioRules[scenarioRules.length - 1]?.attributeName !== '' && (
              <button
                className='mt-2 align-middle font-medium text-lighthouseBlue'
                onClick={addRule}
              >
                <AddIcon className='mr-2' />
                Add Another Field
              </button>
            )}
          </div>
        </OverlayScrollbar>
      </div>
    </ViewModeShell>
  )
}

export default EditScenarioView
